import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mic, MicOff, Grid3x3, PhoneOff, PhoneForwarded, Linkedin, Globe, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { statusEntry, toneStyle } from '@/components/vf/statusMap';
import { outcomeLabel } from '@/services/dialer/callOutcomeLabels';
import { formatClock } from './time';

export interface ProspectRecord {
  id?: string;
  prospect_name?: string | null;
  prospect_phone?: string | null;
  prospect_company?: string | null;
  prospect_title?: string | null;
  prospect_email?: string | null;
  linkedin_url?: string | null;
  website?: string | null;
  notes?: string | null;
  city?: string | null;
}

export interface HistoryRow {
  id?: string;
  call_outcome?: string | null;
  call_status?: string | null;
  call_notes?: string | null;
  created_date?: string | null;
  duration_seconds?: number | null;
  meeting_booked?: boolean | null;
}

interface Props {
  mode: 'live' | 'qualifying' | 'consulted' | 'dialing' | 'paused' | 'idle';
  name: string;
  phone: string;
  company: string;
  startedAt: number | null;
  inbound: boolean;
  prospect: ProspectRecord | null;
  history: HistoryRow[];
  now: number;
  isMuted: boolean;
  keypadOpen: boolean;
  onMute(): void;
  onKeypad(): void;
  onHangup(): void;
  onHangupQualify(): void;
  onBackToCall?: () => void;
}

const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('') || '·';

/**
 * Spec §4.1–4.4: the prospect on the line, call commands on top of the card
 * (never in a bottom bar). A prospect opened from a line is « Consultation »,
 * greyed, and the call stays where it is.
 */
export function ProspectPane(p: Props) {
  const { t } = useTranslation(['dialer']);
  const [tab, setTab] = useState<'card' | 'history'>('card');
  const live = p.mode === 'live';
  const badge = live
    ? { label: p.inbound ? t('dialer:callPage.prospect.inbound') : t('dialer:callPage.prospect.onLine'), color: 'var(--vf-status-connected-fg)' }
    : p.mode === 'consulted'
      ? { label: t('dialer:callPage.prospect.consultation'), color: 'var(--vf-text-3)' }
      : p.mode === 'qualifying' ? { label: t('dialer:callPage.prospect.ended'), color: 'var(--vf-text-2)' } : null;

  const facts = useMemo(() => {
    const r = p.prospect;
    return [
      [t('dialer:callPage.prospect.phone'), p.phone],
      [t('dialer:callPage.prospect.company'), p.company || r?.prospect_company || ''],
      [t('dialer:callPage.prospect.title'), r?.prospect_title || ''],
      [t('dialer:callPage.prospect.city'), r?.city || ''],
    ].filter(([, v]) => !!v) as Array<[string, string]>;
  }, [p.prospect, p.phone, p.company, t]);

  if (p.mode === 'idle' || p.mode === 'dialing' || p.mode === 'paused') {
    return (
      <section className="flex min-w-0 flex-1 items-center justify-center p-6" aria-live="polite">
        <p className="text-sm" style={{ color: 'var(--vf-text-3)' }}>
          {t(p.mode === 'dialing' ? 'dialer:callPage.prospect.dialingPreview' : p.mode === 'paused' ? 'dialer:callPage.prospect.paused' : 'dialer:callPage.prospect.waiting')}
        </p>
      </section>
    );
  }

  return (
    <section
      aria-label={p.name}
      className="flex min-w-0 flex-1 flex-col overflow-y-auto p-4"
      style={{ opacity: p.mode === 'consulted' ? 0.92 : 1 }}
    >
      <div className="flex items-start gap-3">
        <div
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-semibold"
          style={{ background: 'var(--vf-accent-bg)', color: 'var(--vf-accent-text)' }}
          aria-hidden
        >
          {initials(p.name)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="truncate text-lg font-semibold" style={{ color: 'var(--vf-text-1)' }}>{p.name || p.phone}</h2>
            {badge && (
              <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium" style={{ color: badge.color, borderColor: 'currentColor' }}>
                {badge.label}
              </span>
            )}
            {live && p.startedAt && <span className="text-sm tabular-nums" style={{ color: 'var(--vf-text-2)' }}>{formatClock(p.now - p.startedAt)}</span>}
          </div>
          <p className="truncate text-sm" style={{ color: 'var(--vf-text-2)' }}>
            {[p.prospect?.prospect_title, p.company || p.prospect?.prospect_company].filter(Boolean).join(' · ')}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-3 text-xs" style={{ color: 'var(--vf-text-2)' }}>
            <span className="tabular-nums">{p.phone}</span>
            {p.prospect?.linkedin_url && <a className="inline-flex items-center gap-1 underline-offset-2 hover:underline" href={p.prospect.linkedin_url} target="_blank" rel="noreferrer"><Linkedin className="h-3.5 w-3.5" aria-hidden />LinkedIn</a>}
            {p.prospect?.website && <a className="inline-flex items-center gap-1 underline-offset-2 hover:underline" href={p.prospect.website} target="_blank" rel="noreferrer"><Globe className="h-3.5 w-3.5" aria-hidden />{t('dialer:callPage.prospect.website')}</a>}
            {p.prospect?.prospect_email && <span className="inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5" aria-hidden />{p.prospect.prospect_email}</span>}
          </div>
        </div>
      </div>

      {live && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button variant="outline" className="min-h-11" onClick={p.onMute} aria-pressed={p.isMuted} aria-keyshortcuts="M">
            {p.isMuted ? <MicOff className="mr-1.5 h-4 w-4" aria-hidden /> : <Mic className="mr-1.5 h-4 w-4" aria-hidden />}
            {p.isMuted ? t('dialer:callPage.prospect.unmute') : t('dialer:callPage.prospect.mute')}
          </Button>
          <Button variant="outline" className="min-h-11" onClick={p.onKeypad} aria-pressed={p.keypadOpen} aria-keyshortcuts="D">
            <Grid3x3 className="mr-1.5 h-4 w-4" aria-hidden />{t('dialer:callPage.prospect.keypad')}
          </Button>
          <div className="flex-1" />
          <Button variant="outline" className="min-h-11" onClick={p.onHangupQualify} aria-keyshortcuts="Control+P Meta+P">
            <PhoneForwarded className="mr-1.5 h-4 w-4" aria-hidden />{t('dialer:callPage.prospect.hangupQualify')}
          </Button>
          <Button className="min-h-11 bg-[var(--vf-fill-danger)] text-[var(--vf-primary-fg)] hover:brightness-110" onClick={p.onHangup} aria-keyshortcuts="H">
            <PhoneOff className="mr-1.5 h-4 w-4" aria-hidden />{t('dialer:callPage.prospect.hangup')}
          </Button>
        </div>
      )}
      {p.mode === 'consulted' && p.onBackToCall && (
        <Button variant="outline" size="sm" className="mt-3 self-start" onClick={p.onBackToCall}>{t('dialer:callPage.prospect.backToCall')}</Button>
      )}

      <div
        className="mt-5 flex gap-1 border-b"
        role="tablist"
        style={{ borderColor: 'var(--vf-border)' }}
        onKeyDown={(e) => {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          e.preventDefault();
          const nextTab = tab === 'card' ? 'history' : 'card';
          setTab(nextTab);
          document.getElementById(`call-tab-${nextTab}`)?.focus();
        }}
      >
        {(['card', 'history'] as const).map((k) => (
          <button
            key={k}
            id={`call-tab-${k}`}
            role="tab"
            type="button"
            aria-selected={tab === k}
            aria-controls={`call-panel-${k}`}
            tabIndex={tab === k ? 0 : -1}
            onClick={() => setTab(k)}
            className="-mb-px min-h-11 border-b-2 px-3 py-2 text-sm focus-visible:outline focus-visible:outline-2"
            style={{ borderColor: tab === k ? 'var(--vf-accent)' : 'transparent', color: tab === k ? 'var(--vf-text-1)' : 'var(--vf-text-2)' }}
          >
            {k === 'card' ? t('dialer:callPage.prospect.tabCard') : t('dialer:callPage.prospect.tabHistory', { count: p.history.length })}
          </button>
        ))}
      </div>
      {tab === 'card' ? (
        <dl id="call-panel-card" role="tabpanel" aria-labelledby="call-tab-card" className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
          {facts.map(([k, v]) => (
            <div key={k} className="contents">
              <dt style={{ color: 'var(--vf-text-2)' }}>{k}</dt>
              <dd className="min-w-0 break-words" style={{ color: 'var(--vf-text-1)' }}>{v}</dd>
            </div>
          ))}
          {p.prospect?.notes && (
            <div className="col-span-2 mt-2 whitespace-pre-wrap rounded-md p-3 text-sm" style={{ background: 'var(--vf-surface-2)', color: 'var(--vf-text-1)' }}>
              {p.prospect.notes}
            </div>
          )}
        </dl>
      ) : (
        <ul id="call-panel-history" role="tabpanel" aria-labelledby="call-tab-history" className="mt-3 flex flex-col gap-2">
          {p.history.length === 0 && <li className="text-sm" style={{ color: 'var(--vf-text-3)' }}>{t('dialer:callPage.prospect.noHistory')}</li>}
          {p.history.map((h) => {
            const key = h.meeting_booked ? 'meeting_booked' : (h.call_outcome || h.call_status || '');
            const style = toneStyle(statusEntry(key).tone);
            return (
              <li key={h.id} className="rounded-md border p-2 text-sm" style={{ borderColor: 'var(--vf-border)' }}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium" style={{ color: style.fg }}>{outcomeLabel(key, t)}</span>
                  <span className="text-xs tabular-nums" style={{ color: 'var(--vf-text-2)' }}>
                    {h.created_date ? new Date(h.created_date).toLocaleString() : ''}
                  </span>
                </div>
                {h.call_notes && <p className="mt-1 whitespace-pre-wrap" style={{ color: 'var(--vf-text-2)' }}>{h.call_notes}</p>}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
